import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PHOTO_RECEPTION } from "@/data/photos";

/**
 * Editorial split-layout hero image.
 *
 * The receptionist and the returning guests are the visual hero. The image
 * lives on the right (5–7 cols on desktop, full width on mobile) with a warm
 * cinematic gradient so the H1/subheading on the left stay perfectly legible
 * even when the image itself is very warm.
 */
export default function HeroPhoto() {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="hero-photo">
      {/* Ambient dark base — establishes the editorial dark canvas */}
      <div className="absolute inset-0 bg-bht-void" />

      {/* Photo — occupies the right half on desktop, full width on mobile */}
      <motion.div
        initial={{ opacity: 0, scale: 1.04 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-y-0 right-0 w-full lg:w-[62%]"
      >
        <img
          src={PHOTO_RECEPTION}
          alt={t("hp.hero.image_alt")}
          className="h-full w-full object-cover object-center"
          loading="eager"
          decoding="async"
        />
      </motion.div>

      {/* Left → right dark-to-clear gradient to keep the text readable on desktop */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-bht-void via-bht-void/85 to-bht-void/20 lg:to-transparent" />
      {/* Bottom fade for smooth transition into the next section */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-bht-void" />
      {/* Warm gold glow — very subtle hospitality atmosphere */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_45%,rgba(200,160,69,0.10),transparent_65%)]" />
      {/* Fine gold hairline */}
      <div className="pointer-events-none absolute inset-x-0 bottom-24 h-px bg-gradient-to-r from-transparent via-bht-gold/25 to-transparent" />
      {/* Film grain */}
      <div className="pointer-events-none absolute inset-0 grain opacity-25" />
    </div>
  );
}
